/**
 * UploadBanner — sticky progress strip shown while photos upload.
 *
 * Props
 *  queue:     [{ id, name, status: "queued"|"uploading"|"done"|"failed" }]
 *  onRetry:   () => void  — retry all failed items
 *  onDismiss: () => void  — clear the queue / hide the banner
 *  sticky:    boolean     — attach to top of viewport on scroll (default false)
 */
import { Loader2, AlertCircle, RefreshCw, X } from "lucide-react";

export default function UploadBanner({ queue, onRetry, onDismiss, sticky = false }) {
  if (!queue || queue.length === 0) return null;

  const total  = queue.length;
  const done   = queue.filter((q) => q.status === "done").length;
  const failed = queue.filter((q) => q.status === "failed").length;
  const active = queue.filter(
    (q) => q.status === "uploading" || q.status === "queued"
  ).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div
      className={`${
        sticky ? "sticky top-0 z-50" : ""
      } bg-[#0F1115] border-b border-[#1E232D] px-4 pt-3 pb-2.5`}
      data-testid="upload-banner"
    >
      {/* Progress bar */}
      <div className="h-0.5 bg-[#1E232D] rounded-full overflow-hidden mb-2">
        <div
          className={`h-full transition-all duration-500 ease-out ${
            active === 0 && failed === 0 ? "bg-green-500" : "bg-[#E6C982]"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex items-center justify-between gap-3">
        {/* Status text */}
        <div className="flex items-center gap-2 min-w-0">
          {active > 0 && (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-[#E6C982] shrink-0" />
          )}
          {active === 0 && failed > 0 && (
            <AlertCircle className="w-3.5 h-3.5 text-orange-400 shrink-0" />
          )}
          <span
            className={`text-xs truncate ${
              active === 0 && failed > 0 ? "text-orange-300" : "text-white/60"
            }`}
          >
            {active > 0
              ? `Uploading ${done + 1} of ${total}…`
              : failed > 0
              ? `${done} of ${total} uploaded · ${failed} failed`
              : `${done} of ${total} uploaded`}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {failed > 0 && active === 0 && (
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-[#E6C982] hover:text-white border border-[#E6C982]/30 hover:border-white/30 rounded px-2 py-0.5 transition"
              data-testid="upload-banner-retry"
            >
              <RefreshCw className="w-3 h-3" />
              Retry {failed}
            </button>
          )}
          {active === 0 && (
            <button
              onClick={onDismiss}
              className="text-white/30 hover:text-white/60 transition"
              aria-label="Dismiss upload status"
              data-testid="upload-banner-dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
